/**
 * Web Crypto helpers for encrypting PHI at rest in IndexedDB.
 *
 * Threat model (what this defends against):
 *   - Stolen powered-off tablet → IndexedDB folder copied → ciphertext is
 *     opaque without the wrapped DEK (held server-side) + KMS unwrap access.
 *   - Doctor logs out → in-memory DEK is wiped via clearDek(). The encrypted
 *     IndexedDB ciphertext stays on disk so re-login can decrypt the same
 *     records (preserves offline-first UX).
 *
 * What this does NOT defend against:
 *   - Tablet stolen while powered on, unlocked, doctor logged in. The key is
 *     in browser RAM at that point; physical security (screen lock, idle
 *     timeout, full-disk encryption) handles that case.
 *
 * Key handling rules:
 *   - The plaintext DEK is fetched from POST /security/dek once per session,
 *     immediately imported into Web Crypto with `extractable: false`, then
 *     the raw bytes are dropped from JS scope.
 *   - The CryptoKey handle lives only in module-scope memory. It is never
 *     written to IndexedDB, localStorage, sessionStorage, cookies, or any
 *     other persistent surface.
 *   - Concurrent first-call requests are coalesced via `inFlight` so we make
 *     exactly one /security/dek HTTP round-trip per session boot.
 */

import { API_BASE_URL, DEVICE_ID_KEY } from "../constants";
import { apiRequest } from "../services/api.client";
import { logger } from "../utils/logger";
import { isBackendUnreachable } from "../utils/network";
import { db } from "./schema";

const ALGORITHM = "AES-GCM";
const IV_LENGTH_BYTES = 12; // 96 bits — AES-GCM standard.

interface DekResponse {
  dek:        string; // base64-encoded 32-byte AES-256 key
  sync_token: string; // narrow-scope token for SW background push (store in IndexedDB)
}

let dekKey:   CryptoKey | null          = null;
let inFlight: Promise<CryptoKey> | null = null;
// Set to true by clearDek() so the IDB-restore path in getDek() cannot
// race a just-wiped key back into memory before the IDB delete commits.
// Cleared only when fetchAndImportDek() successfully returns a fresh server key.
let dekWiped = false;

function getDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function fetchAndImportDek(): Promise<CryptoKey> {
  const deviceId = getDeviceId();
  try {
    const response = await apiRequest<DekResponse>("/security/dek", {
      method: "POST",
      body: JSON.stringify({ device_id: deviceId }),
    });

    const rawBytes = base64ToBytes(response.dek);
    if (rawBytes.length !== 32) {
      logger.error("crypto.fetchAndImportDek bad key length", undefined, { length: rawBytes.length });
      throw new Error("Encryption key length invalid");
    }

    const key = await crypto.subtle.importKey(
      "raw",
      rawBytes,
      { name: ALGORITHM, length: 256 },
      false,
      ["encrypt", "decrypt"],
    );

    rawBytes.fill(0);
    dekWiped = false; // fresh key from server — allow IDB restore in future refreshes

    // Persist CryptoKey to IDB so offline reads survive a page refresh.
    // CryptoKey is structured-cloneable even when extractable:false — raw bytes
    // are never written to disk; only SubtleCrypto can use the handle.
    db.crypto_keys.put({ id: "dek", key }).catch(() => {
      logger.warn("crypto: failed to persist DEK to IDB");
    });

    // Persist sync_token and api_base_url so the SW can push without an open tab.
    // api_base_url is same-origin so the XSS risk of storing it in IDB is the
    // same as any other same-origin resource the attacker already controls.
    db.device_settings
      .put({ key: "sync_token", value: response.sync_token })
      .catch((err) => logger.warn("crypto: failed to persist sync_token", err));
    db.device_settings
      .put({ key: "api_base_url", value: API_BASE_URL })
      .catch((err) => logger.warn("crypto: failed to persist api_base_url", err));

    // Deliver the API URL to the SW's memory so it can push without a tab.
    // postMessage is not accessible to XSS (SW memory is isolated); IDB is not.
    // Also wire a one-time controllerchange listener so a newly-activated SW
    // (first install, or update) receives the URL even if it wasn't controlling
    // the page when this fetch ran.
    const sendApiUrl = () => {
      navigator.serviceWorker.controller?.postMessage({
        type: "SET_API_BASE_URL",
        url:  API_BASE_URL,
      });
    };
    sendApiUrl();
    navigator.serviceWorker.addEventListener("controllerchange", sendApiUrl, { once: true });

    return key;
  } catch (err) {
    // If the backend is unreachable (offline / backend down), re-throw so the
    // caller gets the correct "DEK unavailable" error. Falling back here would
    // produce a wrong key — any existing Dexie data (encrypted with the real
    // KMS key) would then fail to decrypt with a silent OperationError.
    if (isBackendUnreachable(err)) throw err;

    // Production guard: no fallback key in prod — any non-offline server error
    // (bad KMS ARN, 500, TLS failure) must surface rather than silently encrypt
    // with a weak device-derived key that the backend can never decrypt.
    if (process.env.NODE_ENV === "production") throw err;

    // DEV FALLBACK: backend is reachable but KMS is not configured (KMS_ENABLED=False).
    // Uses the same fixed key as kms.py's _DEV_KEY so the backend can decrypt
    // blobs produced here (and SW batch push works in dev without real KMS).
    // NOT persisted to crypto_keys — a dev key must never survive page refresh
    // into a session that has real KMS configured (would silently decrypt with
    // the wrong key and produce OperationError on every read).
    logger.warn("DEK fetch failed (KMS not configured?), using dev fallback key");
    const devKeyBytes = new TextEncoder().encode("kaagazy-dev-key-local-only-32byt");
    const fallbackKey = await crypto.subtle.importKey(
      "raw",
      devKeyBytes,
      { name: ALGORITHM, length: 256 },
      false,
      ["encrypt", "decrypt"],
    );
    return fallbackKey;
  }
}

/**
 * Get the per-device DEK as a non-extractable Web Crypto handle.
 *
 * First call: fetches /security/dek, imports, caches in module memory.
 * Subsequent calls: returns the cached handle.
 *
 * Concurrent first-calls are coalesced — only one HTTP round-trip happens
 * even if 50 callers hit getDek() simultaneously during boot.
 *
 * Throws if the network/KMS is unavailable. Callers must NOT catch and fall
 * back to plaintext — that would defeat encryption-at-rest.
 */
export async function getDek(): Promise<CryptoKey> {
  if (dekKey) return dekKey;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    // IDB cache — survives page refresh without a server round-trip.
    // CryptoKey is structured-cloneable (Web Crypto §7): stored as an opaque
    // browser-managed handle, never as raw bytes on disk.
    try {
      const stored = await db.crypto_keys.get("dek");
      // dekWiped guard: re-checked after the async IDB read to close the TOCTOU
      // window where clearDek() fires between the check above and here.
      if (stored?.key && !dekWiped) {
        dekKey = stored.key;
        return stored.key;
      }
    } catch { /* IDB unavailable — fall through to server */ }

    const key = await fetchAndImportDek();
    dekKey = key;
    return key;
  })().finally(() => { inFlight = null; });

  return inFlight;
}

/**
 * Wipe the in-memory DEK. Call on logout and on idle-timeout.
 *
 * The IndexedDB ciphertext stays on disk — re-login refetches the same
 * wrapped DEK from the server, unwraps it, and existing local records
 * become decryptable again. This preserves offline-first UX while ensuring
 * the key is not held while the doctor is signed out.
 */
export function clearDek(): void {
  dekWiped = true; // block IDB restore before the async IDB delete commits
  dekKey = null;
  inFlight = null;
  // Wipe both the IDB CryptoKey and the sync_token on logout/idle-timeout.
  // The sync_token has server-side TTL but clearing it immediately closes the
  // window between idle-wipe and expiry.
  db.crypto_keys.delete("dek").catch(() => { /* non-fatal */ });
  db.device_settings.delete("sync_token").catch(() => { /* non-fatal */ });
}

/**
 * Encrypt a plaintext string. Output is base64(IV || ciphertext+authTag),
 * suitable for storage in a Dexie `encrypted_data` string column.
 *
 * Each call uses a fresh random 96-bit IV. IV reuse with AES-GCM under the
 * same key is catastrophic (key recovery), so this MUST stay random per call.
 */
export async function encrypt(plaintext: string): Promise<string> {
  const key = await getDek();
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH_BYTES));
  const plaintextBytes = new TextEncoder().encode(plaintext);

  const ciphertextBuffer = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv },
    key,
    plaintextBytes,
  );
  const ciphertextBytes = new Uint8Array(ciphertextBuffer);

  const combined = new Uint8Array(iv.length + ciphertextBytes.length);
  combined.set(iv, 0);
  combined.set(ciphertextBytes, iv.length);

  return bytesToBase64(combined);
}

/**
 * Decrypt a base64(IV || ciphertext+authTag) blob produced by encrypt().
 *
 * Throws on auth-tag failure (tampered ciphertext) or wrong key (e.g. the
 * device's wrapped DEK was revoked and a new one was minted — old records
 * become permanently unreadable, which is the intended behaviour).
 */
export async function decrypt(payload: string): Promise<string> {
  const key = await getDek();
  const combined = base64ToBytes(payload);

  if (combined.length < IV_LENGTH_BYTES + 1) {
    logger.error("crypto.decrypt payload too short", undefined, { length: combined.length });
    throw new Error("Encrypted payload malformed");
  }

  const iv = combined.subarray(0, IV_LENGTH_BYTES);
  const ciphertext = combined.subarray(IV_LENGTH_BYTES);

  const plaintextBuffer = await crypto.subtle.decrypt(
    { name: ALGORITHM, iv },
    key,
    ciphertext,
  );

  return new TextDecoder().decode(plaintextBuffer);
}

/** Convenience: JSON.stringify + encrypt. Use for object payloads. */
export async function encryptJson<T>(value: T): Promise<string> {
  return encrypt(JSON.stringify(value));
}

/** Convenience: decrypt + JSON.parse. Caller asserts the shape via T. */
export async function decryptJson<T>(payload: string): Promise<T> {
  const plaintext = await decrypt(payload);
  return JSON.parse(plaintext) as T;
}
